/**
 * Nosto-Search.js
 *
 * Inicializa y conecta todos los módulos utilizados por Search.
 *
 * Administra la página de resultados y el autocomplete usando
 * renderers e HitStores independientes, pero compartiendo el cliente,
 * servicio de vistas y FacetManager del SERP.
 */

(function initializeNostoSearch(global) {
    "use strict";

    const SELECTORS = {
        root:
            "[data-nosto-search]",

        form:
            "[data-nosto-search-form]",

        input:
            "[data-nosto-search-input]",

        pageResults:
            "[data-nosto-search-results]",

        modalResults:
            "[data-nosto-search-autocomplete-results]",

        filters:
            "[data-nosto-search-filters]",

        sort:
            "[data-nosto-search-sort]",

        loadMore:
            "[data-nosto-search-load-more]",

        applyFilters:
            "[data-nosto-search-apply-filters]",

        clearFilters:
            "[data-nosto-search-clear-filters]",

        keywords:
            "[data-nosto-search-keywords]",

        keywordsBlock:
            "[data-nosto-search-keywords-block]",

        caption:
            "[data-nosto-search-caption]",

        noResults:
            "[data-nosto-search-no-results]",

        noResultsQuery:
            "[data-nosto-search-no-results-query]",

        noResultsRecommendations:
            "[data-nosto-search-no-results-recommendations]",

        noResultsGrid:
            "[data-nosto-search-no-results-grid]",

        noResultsMore:
            "[data-nosto-search-no-results-more]",

        noResultsMoreButton:
            "[data-nosto-search-no-results-more-button]",
    };

    /**
     * Comprueba que todas las clases necesarias estén disponibles.
     */
    function validateDependencies() {
        const requiredDependencies = [
            "NostoClient",
            "NostoFacetManager",
            "NostoHitStore",
            "NostoProductResultsRenderer",
            "NostoFacetRenderer",
            "NostoProductViewService",
            "NostoQuickBuyModal",
            "NostoSearchController",
        ];

        const missingDependencies =
            requiredDependencies.filter(
                (dependency) =>
                    typeof global[dependency] !==
                    "function"
            );

        if (missingDependencies.length) {
            throw new Error(
                [
                    "No fue posible inicializar Search de Nosto.",
                    `Faltan: ${missingDependencies.join(", ")}.`,
                ].join(" ")
            );
        }
    }

    /**
     * Obtiene la configuración del buscador desde Liquid.
     */
    function getSearchConfig(root) {
        return {
            pageSize:
                Number(
                    root.dataset
                        .nostoPageSize ||
                    20
                ),

            modalSize:
                Number(
                    root.dataset
                        .nostoModalSize ||
                    5
                ),

            keywordSize:
                Number(
                    root.dataset
                        .nostoKeywordSize ||
                    8
                ),

            minimumQueryLength:
                Number(
                    root.dataset
                        .nostoMinimumQueryLength ||
                    2
                ),

            debounceDelay:
                Number(
                    root.dataset
                        .nostoDebounceDelay ||
                    300
                ),

            debug:
                root.dataset.nostoDebug ===
                "true" ||
                global.NOSTO_DEBUG === true,
        };
    }

    /**
     * Crea el cliente compartido de Nosto.
     */
    function createClient(config) {
        return new global.NostoClient({
            debug:
                config.debug,
        });
    }

    /**
     * Crea el manager de filtros utilizado por la página de resultados.
     */
    function createFacetManager(config) {
        const facetManager =
            new global.NostoFacetManager({
                definitions:
                    global.NOSTO_FACET_DEFINITIONS ||
                    [],

                colorGroups:
                    global.NOSTO_COLOR_GROUPS ||
                    {},

                colorFields: [
                    "customFields.custom-color",
                ],

                priceField:
                    "price",

                syncUrl: true,
                urlMode: "replace",

                debug:
                    config.debug,
            });

        if (
            facetManager.hasManagedUrlState()
        ) {
            facetManager.restoreStateFromUrl(
                global.location.href,
                {
                    emit: false,
                    syncUrl: false,
                }
            );
        }

        return facetManager;
    }

    /**
     * Crea el servicio compartido de vistas Liquid.
     */
    function createProductViewService(
        config
    ) {
        return new global
            .NostoProductViewService({
                cardView:
                    "product-card",

                quickBuyView:
                    "nosto-quick-buy",

                concurrency: 4,
                cache: true,

                debug:
                    config.debug,
            });
    }

    /**
 * Crea o reutiliza el modal global de compra rápida.
 */
    function createQuickBuyModal({
        productViewService,
        config,
    }) {
        if (
            global.NOSTO_QUICK_BUY_MODAL
        ) {
            return global
                .NOSTO_QUICK_BUY_MODAL;
        }

        const quickBuyModal =
            new global.NostoQuickBuyModal({
                viewService:
                    productViewService,

                debug:
                    config.debug,
            });

        quickBuyModal.init();

        global.NOSTO_QUICK_BUY_MODAL =
            quickBuyModal;

        return quickBuyModal;
    }

    /**
 * Actualiza el texto visible de la búsqueda actual.
 */
    function updateSearchCaption(
        query
    ) {
        const caption =
            document.querySelector(
                SELECTORS.caption
            );

        if (!caption) {
            return;
        }

        const normalizedQuery =
            String(query ?? "").trim();

        if (!normalizedQuery) {
            caption.textContent = "";
            caption.hidden = true;

            return;
        }

        const queryElement =
            document.createElement(
                "strong"
            );

        queryElement.textContent =
            `“${normalizedQuery}”`;

        caption.replaceChildren(
            "Resultados para ",
            queryElement
        );

        caption.hidden = false;
    }

    /**
     * Crea el renderer visual de facets para el SERP.
     */
    function createFacetRenderer({
        facetManager,
        config,
    }) {
        return new global
            .NostoFacetRenderer({
                container:
                    SELECTORS.filters,

                facetManager,

                labels:
                    global.NOSTO_FACET_LABELS ||
                    {},

                hiddenFields: [
                    "customFields.shasa-color",
                ],

                colorField:
                    "customFields.custom-color",

                priceField:
                    "price",

                sizeField:
                    "skus.customFields.talla",

                sizeOrder:
                    global.NOSTO_SIZE_ORDER ||
                    [],

                fieldOrder: [
                    "skus.customFields.talla",
                    "customFields.custom-color",
                    "customFields.custom-fits",
                    "customFields.custom-product_group_code",
                    "customFields.custom-pg_filtro",
                    "customFields.custom-material",
                    "price",
                ],

                applyButton:
                    SELECTORS.applyFilters,

                clearButton:
                    SELECTORS.clearFilters,

                autoApply: false,
                defaultOpen: true,

                currency:
                    global.Shopify?.currency
                        ?.active ||
                    "MXN",

                currencySymbol: "$",

                showCounts: true,
                showPriceLabels: false,

                onApply() {
                    const root =
                        document.querySelector(
                            SELECTORS.root
                        );

                    const closeControl =
                        root?.querySelector(
                            "[data-collection-sidebar-close]"
                        );

                    closeControl?.click();
                },

                debug:
                    config.debug,
            });
    }

    /**
     * Crea un renderer de productos para Search usando el ProductCard universal.
     */
    function createResultsRenderer({
        container,
        context,
        productViewService,
        getController,
        config,
    }) {
        const isModal =
            context === "modal";

        const normalizeString = (value) =>
            String(value ?? "").trim();

        const normalizeProductId = (value) => {
            const normalized =
                normalizeString(value);

            if (!normalized) {
                return "";
            }

            return normalized
                .split("/")
                .filter(Boolean)
                .at(-1);
        };

        const getHandle = (hit) => {
            const directHandle =
                normalizeString(
                    hit?.handle ||
                    hit?.productHandle
                );

            if (directHandle) {
                return directHandle;
            }

            const url =
                normalizeString(
                    hit?.url ||
                    hit?.productUrl
                );

            if (!url) {
                return "";
            }

            try {
                const parsed =
                    new URL(
                        url,
                        global.location.origin
                    );

                const parts =
                    parsed.pathname
                        .split("/")
                        .filter(Boolean);

                const index =
                    parts.indexOf(
                        "products"
                    );

                return index >= 0
                    ? parts[index + 1] || ""
                    : "";
            } catch (error) {
                return "";
            }
        };

        return new global
            .NostoProductResultsRenderer({
                container,

                placeholderTagName: "li",
                statusTagName: "li",

                emptyMessage:
                    isModal
                        ? "No encontramos sugerencias."
                        : "No encontramos resultados para tu búsqueda.",

                errorMessage:
                    isModal
                        ? "No fue posible cargar las sugerencias."
                        : "No fue posible cargar los resultados. Intenta nuevamente.",

                placeholderClass:
                    "nosto-product-placeholder",

                productItemClass:
                    "grid-item",

                debug:
                    config.debug,

                productCardFactory:
                    async ({
                        hit,
                        productId,
                        container:
                            productContainer,
                        context:
                            rendererContext = {},
                    }) => {
                        const normalizedProductId =
                            normalizeProductId(
                                productId
                            );

                        const handle =
                            getHandle(
                                hit
                            );

                        if (!handle) {
                            throw new Error(
                                `No fue posible obtener el handle para el producto ${normalizedProductId || "desconocido"}.`
                            );
                        }

                        const html =
                            await productViewService
                                .getView({
                                    handle,

                                    productId:
                                        normalizedProductId,

                                    view:
                                        "product-card",

                                    validationSelector:
                                        "[data-product-card]",

                                    context: {
                                        ...rendererContext,

                                        source:
                                            "nosto-search",

                                        track:
                                            context === "modal"
                                                ? "autocomplete"
                                                : "serp",
                                    },
                                });

                        const template =
                            document.createElement(
                                "template"
                            );

                        template.innerHTML =
                            String(html).trim();

                        const sourceCard =
                            template.content
                                .querySelector(
                                    "[data-product-card]"
                                );

                        if (!sourceCard) {
                            throw new Error(
                                "La vista product-card no contiene [data-product-card]."
                            );
                        }

                        const card =
                            sourceCard.cloneNode(
                                true
                            );

                        card.dataset.productSource =
                            "nosto-search";

                        card.dataset.nostoTrack =
                            context === "modal"
                                ? "autocomplete"
                                : "serp";

                        card.dataset
                            .nostoOriginalProductId =
                            normalizedProductId;

                        productContainer
                            .replaceChildren(
                                card
                            );

                        global.initializeProductCards?.(
                            productContainer
                        );

                        const handleCardClick =
                            (event) => {
                                const link =
                                    event.target.closest(
                                        "[data-product-card-link]"
                                    );

                                if (
                                    !link ||
                                    !card.contains(link)
                                ) {
                                    return;
                                }

                                void getController()
                                    ?.recordProductClick(
                                        normalizedProductId,
                                        context,
                                        {
                                            fallbackHit:
                                                hit,
                                        }
                                    );
                            };

                        card.addEventListener(
                            "click",
                            handleCardClick
                        );

                        return {
                            root:
                                card,

                            destroy() {
                                card.removeEventListener(
                                    "click",
                                    handleCardClick
                                );

                                card
                                    .__productCardInstance
                                    ?.destroy?.();
                            },
                        };
                    },
            });
    }

    /**
     * Estado sin resultados del SERP.
     *
     * Muestra el mensaje y carga (una sola vez) el placement de
     * Nosto configurado en la sección. Las cards se marcan con el
     * result_id del placement para que ProductCard.js registre la
     * atribución de clicks y Quick Buy como recomendación.
     */
    function createNoResultsState({
        root,
        productViewService,
    }) {
        const container =
            root.querySelector(
                SELECTORS.noResults
            );

        if (!container) {
            return null;
        }

        const queryElement =
            container.querySelector(
                SELECTORS.noResultsQuery
            );

        /*
         * Las recomendaciones viven fuera del contenedor de
         * "sin resultados" porque también se usan sin búsqueda.
         */
        const recommendationsWrapper =
            root.querySelector(
                SELECTORS.noResultsRecommendations
            );

        const recommendations =
            recommendationsWrapper?.querySelector(
                "nosto-recommendations"
            );

        const grid =
            recommendationsWrapper?.querySelector(
                SELECTORS.noResultsGrid
            );

        const more =
            recommendationsWrapper?.querySelector(
                SELECTORS.noResultsMore
            );

        const moreButton =
            more?.querySelector(
                SELECTORS.noResultsMoreButton
            );

        let recommendationsPromise = null;

        const normalizeString = (value) =>
            String(value ?? "").trim();

        /* ==================================================================
           Ver más / Ver menos
           (mismo comportamiento que Nosto-ProductGrid.js)
           ================================================================== */

        const initialRows =
            Math.max(
                1,
                Number.parseInt(
                    grid?.dataset.initialRows,
                    10
                ) || 2
            );

        let isExpanded = false;
        let lastColumns = 0;

        /*
         * Las columnas salen de la variable --columns del .grid del
         * theme (cambia por media query), así funciona aunque el
         * contenedor siga oculto.
         */
        const getColumns = () => {
            const value =
                Number.parseInt(
                    global
                        .getComputedStyle(grid)
                        .getPropertyValue("--columns"),
                    10
                );

            return Number.isFinite(value) && value > 0
                ? value
                : 1;
        };

        function updateVisibility() {
            if (!grid) {
                return;
            }

            const items =
                Array.from(grid.children);

            const columns =
                getColumns();

            lastColumns =
                columns;

            const initialCount =
                columns * initialRows;

            const canToggle =
                items.length > initialCount;

            const expanded =
                canToggle && isExpanded;

            const visibleCount =
                expanded
                    ? items.length
                    : initialCount;

            const hasMore =
                items.length > visibleCount;

            const peekEnd =
                visibleCount + columns;

            items.forEach((item, index) => {
                const isVisible =
                    index < visibleCount;

                const isPeek =
                    hasMore &&
                    !isVisible &&
                    index < peekEnd;

                item.classList.toggle(
                    "is-hidden",
                    !isVisible && !isPeek
                );

                item.classList.toggle(
                    "is-peek",
                    isPeek
                );

                if (isPeek) {
                    item.setAttribute("aria-hidden", "true");
                    item.setAttribute("inert", "");
                } else {
                    item.removeAttribute("aria-hidden");
                    item.removeAttribute("inert");
                }
            });

            if (!more) {
                return;
            }

            more.hidden =
                !canToggle;

            more.classList.toggle(
                "is-expanded",
                expanded
            );

            if (moreButton) {
                const label =
                    expanded
                        ? moreButton.dataset.labelLess
                        : moreButton.dataset.labelMore;

                if (label) {
                    moreButton.textContent =
                        label;
                }

                moreButton.setAttribute(
                    "aria-expanded",
                    String(expanded)
                );
            }
        }

        moreButton?.addEventListener(
            "click",
            (event) => {
                event.preventDefault();

                isExpanded =
                    !isExpanded;

                updateVisibility();

                /*
                 * Al contraer, si el inicio del grid quedó fuera de
                 * la vista, regresamos a él.
                 */
                if (
                    !isExpanded &&
                    recommendationsWrapper
                        .getBoundingClientRect()
                        .top < 0
                ) {
                    recommendationsWrapper.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            }
        );

        let resizeTimer = null;

        global.addEventListener(
            "resize",
            () => {
                global.clearTimeout(resizeTimer);

                resizeTimer =
                    global.setTimeout(() => {
                        if (
                            grid?.children.length &&
                            getColumns() !== lastColumns
                        ) {
                            updateVisibility();
                        }
                    }, 150);
            }
        );

        const getHandle = (product) => {
            const handle =
                normalizeString(
                    product?.handle ??
                    product?.productHandle ??
                    product?.product_handle
                );

            if (handle) {
                return handle;
            }

            try {
                const url =
                    normalizeString(
                        product?.url ??
                        product?.productUrl ??
                        product?.product_url
                    );

                return (
                    new URL(
                        url,
                        global.location.origin
                    ).pathname.match(
                        /\/products\/([^/?#]+)/
                    )?.[1] || ""
                );
            } catch (error) {
                return "";
            }
        };

        async function renderCard(
            product,
            recommendationContext
        ) {
            const item =
                document.createElement("li");

            item.className =
                "grid-item";

            try {
                const html =
                    await productViewService
                        .getCard({
                            handle:
                                getHandle(product),

                            url:
                                normalizeString(
                                    product?.url
                                ),

                            productId:
                                normalizeString(
                                    product?.productId ??
                                    product?.product_id ??
                                    product?.id
                                ),

                            view:
                                "product-card",

                            context: {
                                source:
                                    "search-no-results",

                                track:
                                    "recommendation",

                                attributionKey:
                                    recommendationContext.resultId,

                                ...recommendationContext,
                            },
                        });

                const template =
                    document.createElement(
                        "template"
                    );

                template.innerHTML =
                    String(html).trim();

                const card =
                    template.content
                        .querySelector(
                            "[data-product-card]"
                        );

                if (!card) {
                    return null;
                }

                card.dataset.productSource =
                    "nosto-recommendation";

                card.dataset.nostoTrack =
                    "recommendation";

                card.dataset.nostoPlacementId =
                    recommendationContext.placementId;

                card.dataset.nostoResultId =
                    recommendationContext.resultId;

                card.dataset.nostoDivId =
                    recommendationContext.divId;

                card.dataset.nostoAttributionKey =
                    recommendationContext.resultId;

                item.appendChild(card);

                return item;
            } catch (error) {
                console.error(
                    "[Nosto-Search] No se pudo renderizar la recomendación.",
                    {
                        product,
                        error,
                    }
                );

                return null;
            }
        }

        async function loadRecommendations() {
            if (
                !recommendations ||
                !grid ||
                typeof recommendations.load !==
                "function"
            ) {
                return;
            }

            const detail =
                await recommendations.load();

            const products =
                detail?.recommendedProducts ?? [];

            if (!products.length) {
                return;
            }

            const recommendationContext = {
                placementId:
                    normalizeString(
                        detail.placementId ||
                        recommendations.placementId
                    ),

                resultId:
                    normalizeString(
                        detail.resultId
                    ),

                divId:
                    normalizeString(
                        detail.divId
                    ),
            };

            const items =
                (
                    await Promise.all(
                        products.map((product) =>
                            renderCard(
                                product,
                                recommendationContext
                            )
                        )
                    )
                ).filter(Boolean);

            if (!items.length) {
                return;
            }

            grid.replaceChildren(...items);

            isExpanded = false;
            updateVisibility();

            global.initializeProductCards?.(
                grid
            );

            recommendationsWrapper.hidden =
                false;
        }

        /*
         * Invalida un show() pendiente cuando llega otra búsqueda
         * mientras las recomendaciones siguen cargando.
         */
        let showToken = 0;

        /*
         * Límite de espera para no dejar el loader indefinidamente
         * si el placement tarda demasiado.
         */
        const RECOMMENDATIONS_TIMEOUT = 8000;

        /**
         * Carga las recomendaciones (una sola vez) y espera a que
         * estén listas. Devuelve false si otra búsqueda o estado
         * invalidó la espera.
         */
        async function waitForRecommendations() {
            const token =
                ++showToken;

            if (!recommendationsPromise) {
                recommendationsPromise =
                    loadRecommendations()
                        .catch((error) => {
                            recommendationsPromise =
                                null;

                            console.error(
                                "[Nosto-Search] No fue posible cargar las recomendaciones sin resultados.",
                                error
                            );
                        });
            }

            await Promise.race([
                recommendationsPromise,

                new Promise((resolve) =>
                    global.setTimeout(
                        resolve,
                        RECOMMENDATIONS_TIMEOUT
                    )
                ),
            ]);

            return token === showToken;
        }

        return {
            /**
             * Espera a que las recomendaciones estén renderizadas y
             * muestra mensaje + recomendaciones al mismo tiempo.
             */
            async show(query) {
                root.classList.remove(
                    "is-search-idle",
                    "is-search-idle-ready"
                );

                if (
                    !(await waitForRecommendations())
                ) {
                    return;
                }

                if (queryElement) {
                    queryElement.textContent =
                        normalizeString(query);
                }

                container.hidden = false;

                root.classList.add(
                    "is-search-empty"
                );

                root.classList.remove(
                    "is-search-pending"
                );
            },

            /**
             * Sin búsqueda: oculta caption/filtros/grid de inmediato
             * y muestra el texto + recomendaciones cuando están listas.
             */
            async showIdle() {
                container.hidden = true;

                root.classList.remove(
                    "is-search-empty",
                    "is-search-pending",
                    "is-search-idle-ready"
                );

                root.classList.add(
                    "is-search-idle"
                );

                if (
                    !(await waitForRecommendations())
                ) {
                    return;
                }

                root.classList.add(
                    "is-search-idle-ready"
                );
            },

            hide() {
                showToken++;

                container.hidden = true;

                root.classList.remove(
                    "is-search-empty",
                    "is-search-idle",
                    "is-search-idle-ready"
                );
            },
        };
    }

    /**
 * Renderiza las keywords en el único contenedor de sugerencias.
 */
    function renderKeywords(
        keywords,
        {
            controller,
        }
    ) {
        const container =
            document.querySelector(
                SELECTORS.keywords
            );

        if (!container) {
            return;
        }

        const block =
            container.closest(
                SELECTORS.keywordsBlock
            );

        container.replaceChildren();

        const normalizedKeywords =
            Array.isArray(keywords)
                ? [
                    ...new Set(
                        keywords
                            .map((keyword) =>
                                String(
                                    keyword ?? ""
                                ).trim()
                            )
                            .filter(Boolean)
                    ),
                ]
                : [];

        if (!normalizedKeywords.length) {
            container.hidden = true;

            if (block) {
                block.hidden = true;
            }

            return;
        }

        const fragment =
            document.createDocumentFragment();

        for (
            const keyword of
            normalizedKeywords
        ) {
            const button =
                document.createElement(
                    "button"
                );

            button.type = "button";

            button.className =
                "nosto-search-keyword";

            button.textContent =
                keyword;

            button.dataset.nostoKeyword =
                keyword;

            /**
             * Ejecuta una búsqueda con la keyword seleccionada.
             */
            button.addEventListener(
                "click",
                () => {
                    controller.submitSearch(
                        keyword,
                        {
                            isKeyword: true,
                        }
                    );
                }
            );

            fragment.appendChild(
                button
            );
        }

        container.appendChild(
            fragment
        );

        container.hidden = false;

        if (block) {
            block.hidden = false;
        }
    }

    /**
     * Obtiene la query inicial desde el input o desde la URL.
     */
    function getInitialQuery(input) {
        const inputValue =
            String(
                input?.value || ""
            ).trim();

        if (inputValue) {
            return inputValue;
        }

        const url =
            new URL(
                global.location.href
            );

        return String(
            url.searchParams.get("q") ||
            ""
        ).trim();
    }


    /**
     * Inicializa todos los componentes de Search.
     */
    async function init() {
        const root =
            document.querySelector(
                SELECTORS.root
            );

        if (!root) {
            return null;
        }

        if (
            root.dataset.nostoInitialized ===
            "true" ||
            root.dataset.nostoInitializing ===
            "true"
        ) {
            return (
                global.NOSTO_SEARCH ||
                null
            );
        }

        root.dataset.nostoInitializing =
            "true";

        validateDependencies();

        const input =
            document.querySelector(
                SELECTORS.input
            );

        const pageResults =
            document.querySelector(
                SELECTORS.pageResults
            );

        const modalResults =
            document.querySelector(
                SELECTORS.modalResults
            );

        if (
            !input ||
            !pageResults ||
            !modalResults
        ) {
            throw new Error(
                "Search requiere input, resultados de página y resultados de autocomplete."
            );
        }

        const config =
            getSearchConfig(root);

        const client =
            createClient(config);

        const facetManager =
            createFacetManager(config);

        const pageHitStore =
            new global.NostoHitStore({
                debug:
                    config.debug,
            });

        const modalHitStore =
            new global.NostoHitStore({
                debug:
                    config.debug,
            });

        const productViewService =
            createProductViewService(
                config
            );


        const quickBuyModal =
            createQuickBuyModal({
                productViewService,
                config,
            });

        const facetRenderer =
            createFacetRenderer({
                facetManager,
                config,
            });

        const noResultsState =
            createNoResultsState({
                root,
                productViewService,
            });

        let controller = null;

        const pageRenderer =
            createResultsRenderer({
                container:
                    pageResults,

                context:
                    "page",

                productViewService,

                getController() {
                    return controller;
                },

                config,
            });

        const modalRenderer =
            createResultsRenderer({
                container:
                    modalResults,

                context:
                    "modal",

                productViewService,

                getController() {
                    return controller;
                },

                config,
            });

        controller =
            new global
                .NostoSearchController({
                    client,
                    facetManager,

                    pageHitStore,
                    modalHitStore,

                    pageRenderer,
                    modalRenderer,

                    input,

                    sortElement:
                        SELECTORS.sort,

                    loadMoreElement:
                        SELECTORS.loadMore,

                    pageSize:
                        config.pageSize,

                    modalSize:
                        config.modalSize,

                    keywordSize:
                        config.keywordSize,

                    minimumQueryLength:
                        config.minimumQueryLength,

                    debounceDelay:
                        config.debounceDelay,

                    productFields: [
                        "name",
                        "url",
                        "imageUrl",
                    ],

                    modalProductFields: [
                        "name",
                        "url",
                        "imageUrl",
                    ],

                    debug:
                        config.debug,

                    /**
 * Renderiza los facets del SERP y confirma el estado aplicado.
 */
                    onFacets(facets) {
                        facetRenderer.render(
                            facets,
                            {
                                markApplied: true,
                            }
                        );
                    },

                    /**
 * Refleja la carga independiente de página y autocomplete.
 */
                    onLoadingChange({
                        context,
                        isLoading,
                    }) {
                        root.classList.toggle(
                            `is-loading-${context}`,
                            isLoading
                        );

                        root.dataset[
                            context === "modal"
                                ? "nostoModalLoading"
                                : "nostoPageLoading"
                        ] =
                            String(isLoading);

                        if (context === "page") {
                            root.setAttribute(
                                "aria-busy",
                                String(isLoading)
                            );
                        }
                    },

                    /**
                     * Al iniciar una consulta nueva del SERP ocultamos
                     * filtros, caption y el estado sin resultados hasta
                     * que llegue la respuesta. Cambios de filtro u orden
                     * conservan la barra para no cerrar el drawer.
                     */
                    onSearchStart({
                        isNewQuery,
                    }) {
                        /*
                         * Sale de los estados sin búsqueda / sin
                         * resultados e invalida esperas pendientes.
                         */
                        noResultsState?.hide();

                        root.classList.remove(
                            "is-search-idle",
                            "is-search-idle-ready"
                        );

                        if (!isNewQuery) {
                            return;
                        }

                        root.classList.add(
                            "is-search-pending"
                        );
                    },

                    /**
                     * Renderiza keywords según el contexto.
                     */
                    onKeywords(
                        keywords,
                        detail
                    ) {
                        renderKeywords(
                            keywords,
                            detail
                        );
                    },

                    /**
 * Actualiza la interfaz y emite un evento con la respuesta de Search.
 */
                    onResponse(
                        response,
                        detail
                    ) {
                        root.classList.remove(
                            `has-error-${detail.context}`
                        );

                        if (
                            detail.context === "page"
                        ) {
                            const pageQuery =
                                detail.controller
                                    ?.state
                                    ?.page
                                    ?.query ||
                                input.value;

                            updateSearchCaption(
                                pageQuery
                            );

                            /*
                             * Sin productos y sin filtros activos:
                             * mostramos recomendaciones en lugar de
                             * filtros y grid. Con filtros activos se
                             * conserva la UI para poder quitarlos.
                             */
                            const isEmpty =
                                detail.mode !== "append" &&
                                !response?.products
                                    ?.hits?.length &&
                                !facetManager
                                    .hasActiveFilters();

                            /*
                             * Con resultados se revela todo junto
                             * (filtros, caption y grid). Sin
                             * resultados, show() mantiene el loader
                             * hasta tener las recomendaciones.
                             */
                            if (
                                isEmpty &&
                                noResultsState
                            ) {
                                void noResultsState.show(
                                    pageQuery
                                );
                            } else {
                                if (
                                    detail.mode !== "append"
                                ) {
                                    noResultsState?.hide();
                                }

                                root.classList.remove(
                                    "is-search-pending"
                                );
                            }
                        }

                        global.refreshProductCards?.(
                            detail.context === "modal"
                                ? modalResults
                                : pageResults
                        );

                        root.dispatchEvent(
                            new CustomEvent(
                                "nosto:search:response",
                                {
                                    bubbles: true,

                                    detail: {
                                        response,
                                        ...detail,
                                    },
                                }
                            )
                        );
                    },

                    /**
                     * Emite un evento cuando ocurre un error.
                     */
                    onError(error, detail) {
                        const context =
                            detail.context ||
                            "page";

                        root.classList.add(
                            `has-error-${context}`
                        );

                        if (context === "page") {
                            root.classList.remove(
                                "is-search-pending"
                            );
                        }
                        root.dispatchEvent(
                            new CustomEvent(
                                "nosto:search:error",
                                {
                                    bubbles: true,

                                    detail: {
                                        error,
                                        ...detail,
                                    },
                                }
                            )
                        );
                    },
                });

        controller.init();

        const form =
            document.querySelector(
                SELECTORS.form
            );

        form?.addEventListener(
            "submit",
            (event) => {
                event.preventDefault();

                controller.submitSearch();
            }
        );

        /**
         * Muestra el estado sin búsqueda (texto + recomendaciones).
         */
        const showIdleState = () => {
            if (noResultsState) {
                void noResultsState.showIdle();

                return;
            }

            root.classList.remove(
                "is-search-pending"
            );

            root.classList.add(
                "is-search-idle",
                "is-search-idle-ready"
            );
        };

        /**
         * Sin búsqueda: limpia el SERP y muestra el texto que
         * invita a buscar en lugar de caption, filtros y grid.
         */
        const enterIdleState = () => {
            if (
                String(input.value || "").trim()
            ) {
                return;
            }

            controller.clearContext(
                "page"
            );

            updateSearchCaption("");

            showIdleState();

            try {
                const url =
                    new URL(
                        global.location.href
                    );

                if (url.searchParams.has("q")) {
                    url.searchParams.delete("q");

                    global.history.replaceState(
                        global.history.state,
                        "",
                        url.pathname +
                        url.search +
                        url.hash
                    );
                }
            } catch (error) {
                // Si falla, la URL conserva la búsqueda anterior.
            }
        };

        input.addEventListener(
            "input",
            enterIdleState
        );

        /*
         * El evento reset se dispara antes de vaciar los campos.
         */
        form?.addEventListener(
            "reset",
            () => {
                global.setTimeout(
                    enterIdleState,
                    0
                );
            }
        );

        global.NOSTO_SEARCH = {
            client,
            facetManager,

            pageHitStore,
            modalHitStore,

            pageRenderer,
            modalRenderer,

            facetRenderer,
            productViewService,
            quickBuyModal,
            controller,
        };


        const initialQuery =
            getInitialQuery(input);

        if (
            initialQuery.length >=
            config.minimumQueryLength
        ) {
            input.value =
                initialQuery;

            await controller.search(
                "page",
                {
                    query:
                        initialQuery,

                    mode:
                        "replace",

                    submit:
                        false,

                    includeFacets:
                        true,
                }
            );
        } else {
            /*
             * Sin búsqueda inicial válida no habrá respuesta que
             * quite el estado pendiente: se muestra el estado idle.
             */
            showIdleState();
        }

        root.dataset.nostoInitialized =
            "true";

        delete root.dataset
            .nostoInitializing;

        return global.NOSTO_SEARCH;
    }

    /**
 * Ejecuta la inicialización cuando el DOM está disponible.
 */
    function boot() {
        init().catch((error) => {
            const root =
                document.querySelector(
                    SELECTORS.root
                );

            if (root) {
                delete root.dataset
                    .nostoInitializing;

                root.classList.remove(
                    "is-search-pending"
                );

                /*
                 * Si falla la inicialización, al menos mostramos
                 * el texto que invita a buscar.
                 */
                if (
                    root.classList.contains(
                        "is-search-idle"
                    )
                ) {
                    root.classList.add(
                        "is-search-idle-ready"
                    );
                }
            }

            console.error(
                "[Nosto-Search] No fue posible inicializar Search.",
                error
            );
        });
    }

    if (
        document.readyState ===
        "loading"
    ) {
        document.addEventListener(
            "DOMContentLoaded",
            boot,
            {
                once: true,
            }
        );
    } else {
        boot();
    }
})(window);